import { CreateContactForUserProfileSource } from '#app/__generated__/api.js';
import { useSaveUserAsContactCreateMutation } from '#app/__generated__/relay/useSaveUserAsContactCreateMutation.graphql.js';
import { useSaveUserAsContactCreateV2Mutation } from '#app/__generated__/relay/useSaveUserAsContactCreateV2Mutation.graphql.js';
import { ContraImage } from '#app/components/Avatars/Avatar/types.js';
import {
  OnListUpdatedArgs,
  useAddToUserList,
} from '#app/components/Network/hooks/useAddToUserList.js';
import { useNetworkArchiveActions } from '#app/components/NetworkTable/components/useNetworkArchiveActions.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useFeatureFlag } from '#app/hooks/useFeatureFlag.js';
import { graphql } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useState } from 'react';

type UseSaveUserAsContactProps = {
  analyticsSection: string;
  contactId: null | string;
  onListUpdated?: (args: OnListUpdatedArgs) => void;
  skipNetworkArchiveActionsToasts?: boolean;
  source: CreateContactForUserProfileSource;
  userProfile: {
    avatarImage: ContraImage | null;
    firstName?: string;
    fullName: string;
    id: string;
    isSavedContact: boolean;
  };
};

type UseSaveUserAsContactV2Props = {
  analyticsSection: string;
  contactId: null | string;
  source: CreateContactForUserProfileSource;
  userProfile: {
    avatarImage: ContraImage | null;
    firstName?: string;
    fullName: string;
    id: string;
    isSavedContact: boolean;
  };
};

const useSaveUserAsContactV1 = ({
  contactId,
  onListUpdated,
  skipNetworkArchiveActionsToasts = false,
  source,
  userProfile,
}: UseSaveUserAsContactProps) => {
  const toast = useToast();
  const [isSavedContact, setIsSavedContact] = useState(
    userProfile.isSavedContact ?? false,
  );

  const [createContactForUserProfile] =
    useContraMutation<useSaveUserAsContactCreateMutation>(graphql`
      mutation useSaveUserAsContactCreateMutation(
        $input: CreateContactForUserProfileInput!
      ) {
        createContactForUserProfile(input: $input) {
          errors {
            __typename
            message
          }
          userProfile {
            id
            avatarImage {
              id
              uid
              url
            }
            isSavedContact
            firstName
            lastName
            contact {
              id
            }
          }
        }
      }
    `);

  const handleCreateContact = useCallback(
    (args?: { onSuccess?: () => void }) => {
      createContactForUserProfile({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            args?.onSuccess?.();
          }
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input: {
            source,
            userProfileId: userProfile.id,
          },
        },
      });
    },
    [createContactForUserProfile, source, toast, userProfile.id],
  );

  const { archiveContact: handleArchiveContact } = useNetworkArchiveActions({
    selectedContacts: contactId
      ? [
          {
            contactId,
            fullName: userProfile.fullName,
            isSavedContact: userProfile.isSavedContact,
            userProfileId: userProfile.id,
          },
        ]
      : [],
    selectedContactsWithUserProfiles: contactId
      ? [
          {
            contactId,
            fullName: userProfile.fullName,
            isSavedContact: userProfile.isSavedContact,
            userProfileId: userProfile.id,
          },
        ]
      : [],
    skipToast: skipNetworkArchiveActionsToasts,
  });

  const { handleOpenAddToListModal } = useAddToUserList({
    addContact: handleCreateContact,
    onListUpdated,
    removeContact: handleArchiveContact,
  });

  const saveUserAsContact = useCallback(() => {
    setIsSavedContact(true);

    handleOpenAddToListModal({
      userProfiles: [
        {
          avatarImage: userProfile.avatarImage,
          contactId,
          fullName: userProfile.fullName,
          id: userProfile.id,
          isSavedContact: userProfile.isSavedContact,
        },
      ],
    });
  }, [
    contactId,
    handleOpenAddToListModal,
    userProfile.avatarImage,
    userProfile.fullName,
    userProfile.id,
    userProfile.isSavedContact,
  ]);

  const removeUserAsContact = useCallback(() => {
    if (!contactId) {
      return;
    }

    setIsSavedContact(false);

    handleOpenAddToListModal({
      userProfiles: [
        {
          avatarImage: userProfile.avatarImage,
          contactId,
          fullName: userProfile.fullName,
          id: userProfile.id,
          isSavedContact: true,
        },
      ],
    });
  }, [
    contactId,
    handleOpenAddToListModal,
    userProfile.avatarImage,
    userProfile.fullName,
    userProfile.id,
  ]);

  return {
    isUserSavedContact: isSavedContact,
    removeUserAsContact,
    saveUserAsContact,
  };
};

const useSaveUserAsContactV2 = ({
  contactId,
  source,
  userProfile,
}: UseSaveUserAsContactV2Props) => {
  const toast = useToast();

  const [createContactForUserProfile] =
    useContraMutation<useSaveUserAsContactCreateV2Mutation>(graphql`
      mutation useSaveUserAsContactCreateV2Mutation(
        $input: CreateContactForUserProfileInput!
      ) {
        createContactForUserProfile(input: $input) {
          errors {
            __typename
            message
          }
          userProfile {
            id
            isSavedContact
            contact {
              id
            }
          }
        }
      }
    `);

  const {
    archiveContact: handleArchiveContact,
    unarchiveContact: handleUnarchiveContact,
  } = useNetworkArchiveActions({
    selectedContacts: contactId
      ? [
          {
            contactId,
            fullName: userProfile.fullName,
            isSavedContact: userProfile.isSavedContact,
            userProfileId: userProfile.id,
          },
        ]
      : [],
    selectedContactsWithUserProfiles: contactId
      ? [
          {
            contactId,
            fullName: userProfile.fullName,
            isSavedContact: userProfile.isSavedContact,
            userProfileId: userProfile.id,
          },
        ]
      : [],
    skipToast: true,
  });

  const saveUserAsContact = useCallback(
    (args?: { onSuccess?: () => void }) => {
      if (userProfile.isSavedContact) {
        return;
      }

      if (contactId) {
        handleUnarchiveContact({
          onSuccess: args?.onSuccess,
        });

        return;
      }

      createContactForUserProfile({
        onCompleted: (errors) => {
          const { apiErrors, hasErrors, payloadErrors } = errors;
          if (hasErrors) {
            if (payloadErrors) {
              toast.error('An unexpected error occurred. Please try again.');
              captureException(payloadErrors[0]);
            } else if (apiErrors) {
              toast.error(apiErrors[0].message);
            }
          } else {
            args?.onSuccess?.();
          }
        },
        onError: (error) => {
          captureException(error);
        },
        variables: {
          input: {
            source,
            userProfileId: userProfile.id,
          },
        },
      });
    },
    [
      contactId,
      createContactForUserProfile,
      handleUnarchiveContact,
      source,
      toast,
      userProfile.id,
      userProfile.isSavedContact,
    ],
  );

  return {
    isUserSavedContact: userProfile.isSavedContact,
    removeUserAsContact: handleArchiveContact,
    saveUserAsContact,
  };
};

export const useSaveUserAsContact = (args: UseSaveUserAsContactProps) => {
  const reworkedSaveToListsFlow = useFeatureFlag('reworkedSaveToListsFlow');

  const v1 = useSaveUserAsContactV1(args);
  const v2 = useSaveUserAsContactV2(args);

  if (reworkedSaveToListsFlow) {
    return v2;
  }

  return v1;
};
